import { useFragment } from '@apollo/client/react';
import { useMemo } from 'react';

import { getMemberIdWithFallback } from '@trello/authentication';
import { getMyOwnedBoards } from '@trello/business-logic/board';

import {
  MyOwnedBoardsFragmentDoc,
  type MyOwnedBoardsFragment,
} from './MyOwnedBoardsFragment.generated';

type Organization = NonNullable<MyOwnedBoardsFragment>['organizations'][number];

export const useMyOwnedBoards = (): ReturnType<typeof getMyOwnedBoards> => {
  const memberId = getMemberIdWithFallback();

  const { data, complete } = useFragment({
    fragment: MyOwnedBoardsFragmentDoc,
    fragmentName: 'MyOwnedBoards',
    from: {
      id: memberId,
      __typename: 'Member',
    },
  });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const member = complete ? data : undefined;

  const boardsWithOrganizations = useMemo(() => {
    if (!member?.organizations) {
      return [];
    }

    const boards = [];
    const organizationsMap = new Map<string, Organization>();
    member.organizations.forEach((organization) =>
      organizationsMap.set(organization.id, organization),
    );

    // oxlint-disable-next-line no-unsafe-optional-chaining
    for (const board of member?.boards) {
      boards.push({
        ...board,
        organization: board.idOrganization
          ? (organizationsMap.get(board.idOrganization) ?? null)
          : null,
      });
    }

    return boards;
  }, [member]);

  if (!member?.boards) {
    return [];
  }

  return getMyOwnedBoards({
    ...member,
    boards: boardsWithOrganizations,
  });
};
